import { Box, Modal, Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  CustomButton,
  CustomDateField,
  CustomDropdown,
  CustomLoader,
  FormModalTitle,
} from "../../../components";
import SigninTextInput from "../../../components/SigninTextInput";
import i18n from "../../../i18n-config";
import { createAsyncPromotion, Promotion } from "../../../store/features/promotionSlice";
import theme from "../../../theme/theme";
import { CIBLES, REGLES } from "../../../utils/constants";
import notifyMessage from "../../../utils/notify";
import { capitalizeFirstLetter } from "../../../utils/strings";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: 400, md: 550, lg: 550 },
  backgroundColor: "#ffffff",
  borderRadius: 2,
  padding: 3,
};

interface Props {
  open: boolean;
  onClose(): void;
}

function PromotionForm({ open, onClose }: Props) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Promotion>({
    cible: "",
    code: "",
    dateDebut: new Date().toLocaleDateString('en-CA'),
    dateFin: "",
    libelle: "",
    regle: "",
    taux: true,
    valeur: 0,
  });

  const onChangeField = (key: string, value: string | number) => {
    setFormData({
      ...formData,
      [key]: value,
    });
  };

  const handleClose = () => {
    onClose();
  };

  const callback = () => {
    setLoading(false);
    handleClose();
    notifyMessage({
      message: capitalizeFirstLetter(i18n.t("create_success")),
      severity: "success",
    });
  };

  const onError = () => {
    setLoading(false);
    notifyMessage({
      message: capitalizeFirstLetter(i18n.t("error_occured")),
      severity: "error",
    });
  };

  const toggleLoader = () => {
    setLoading(!loading);
  };

  const onSave = () => {
    // console.log('####',formData)
    // return
    toggleLoader();
    dispatch(createAsyncPromotion(formData, callback, onError) as any);
  };

  const disabled =
    !formData.cible.trim() ||
    !formData.regle.trim() ||
    !formData.code.trim() ||
    formData.valeur <= 0;

  return (
    <Modal open={open} onClose={handleClose}>
      <form>
        <Box sx={style}>
          <FormModalTitle
            title={i18n.t("creer_promotion")}
            onClose={handleClose}
          />
          <SigninTextInput
            title={capitalizeFirstLetter(i18n.t("libelle"))}
            value={formData.libelle}
            onChange={(value: string) => onChangeField("libelle", value)}
          />
          <Stack direction={"row"} justifyContent="space-between">
            <CustomDateField
              value={formData.dateDebut}
              title={capitalizeFirstLetter(i18n.t("date_debut"))}
              onChange={(value: string) =>
                onChangeField("dateDebut", new Date(value).toLocaleDateString('en-CA'))
              }
              rootStyle={{ marginRight: 2 }}
            />
            <CustomDateField
              value={formData.dateFin}
              title={capitalizeFirstLetter(i18n.t("date_fin"))}
              onChange={(value: string) =>
                onChangeField("dateFin", new Date(value).toLocaleDateString('en-CA'))
              }
              rootStyle={{ marginLeft: 2 }}
            />
          </Stack>
          <CustomDropdown
            title={""}
            value={formData.regle}
            placeholder={"-- " + i18n.t("definissez_regle")}
            selectStyle={{ backgroundColor: "#3D423F", color: "white" }}
            placeholderStyle={{ top: 25 }}
            items={REGLES}
            handleChange={(value: string) => onChangeField("regle", value)}
          />
          <Stack direction={"row"}>
            <SigninTextInput
              title={capitalizeFirstLetter(i18n.t("taux_reduction"))}
              value={"" + formData.valeur}
              type="number"
              endIcon={
                <Box mt={0.5}>
                  <Typography fontSize={20}>%</Typography>
                </Box>
              }
              onChange={(value: string) =>
                onChangeField("valeur", Number(value))
              }
            />
            <CustomDropdown
              title={capitalizeFirstLetter(i18n.t("categorie_sous"))}
              value={formData.cible}
              containerStyle={{ marginLeft: 2 }}
              titleStyle={{ color: "white" }}
              placeholder={"-- " + i18n.t("cible")}
              placeholderStyle={{ top: 45 }}
              selectStyle={{ backgroundColor: "#3D423F", color: "white" }}
              items={CIBLES}
              handleChange={(value: string) => onChangeField("cible", value)}
            />
          </Stack>
          <SigninTextInput
            title={capitalizeFirstLetter(i18n.t("code_promo"))}
            value={formData.code}
            onChange={(value: string) => onChangeField("code", value)}
          />
          <CustomButton
            title={capitalizeFirstLetter(i18n.t("creer_la_promotion"))}
            onClick={onSave}
            disabled={disabled}
            rootStyle={{
              width: "100%",
              height: 50,
              marginTop: 3,
              marginBottom: 2,
              backgroundColor: disabled
                ? theme.colors.disabled
                : theme.colors.primary,
            }}
            titleStyle={{ color: theme.colors.white }}
          />
        </Box>
        <CustomLoader open={loading} handleClose={toggleLoader} />
      </form>
    </Modal>
  );
}

export default PromotionForm;
